import { useEffect } from "react";
import { useLingui } from "@lingui/react/macro";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { useFormContext, type FieldValues } from "react-hook-form";
import { TemplateTimeSlots } from "./TemplateTimeSlots";
import { recurrencesPerDayOptions } from "./timeSlotsUtils";
import { TemplateTimeSensitivityPicker } from "./TemplateTimeSensitivityPicker";
import {
  useFieldErrorWithPrefix,
  useFieldPathWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import { useIsInFinalValidationPhase } from "@/forms/AddActivity/TemplateFieldValidationContext";
import type { TemplateTimeSlotsPickerFields } from "@/forms/AddActivity/templateFormSchema";

export const TemplateTimeSlotsPicker = ({
  isRecurring,
  size = "default",
}: {
  isRecurring: boolean;
  size?: "default" | "compact";
}) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
    unregister,
    resetField,
    watch,
  } = useFormContext<FieldValues>();

  const timeCategoryFieldName =
    useFieldPathWithPrefix<TemplateTimeSlotsPickerFields>("timeCategory");
  const recurrencesPerDayFieldName =
    useFieldPathWithPrefix<TemplateTimeSlotsPickerFields>("recurrencesPerDay");
  const timeSensitivityFieldName =
    useFieldPathWithPrefix<TemplateTimeSlotsPickerFields>("timeSensitivity");
  const timeslotsFieldName =
    useFieldPathWithPrefix<TemplateTimeSlotsPickerFields>("timeslots");
  const timeCategoryError =
    useFieldErrorWithPrefix<TemplateTimeSlotsPickerFields>(
      errors,
      "timeCategory",
    );
  const recurrencesPerDayError =
    useFieldErrorWithPrefix<TemplateTimeSlotsPickerFields>(
      errors,
      "recurrencesPerDay",
    );
  const isInFinalValidationPhase = useIsInFinalValidationPhase();

  const selectedTimeCategory = watch(timeCategoryFieldName);
  const selectedRecurrencesPerDay = watch(recurrencesPerDayFieldName);

  const isAnyTimeOfDay = selectedTimeCategory === timeOfDaySchema.enum.Any;

  const isOneTimeActivityWithSpecificTime =
    !isRecurring && selectedTimeCategory === timeOfDaySchema.enum.Specific;
  const isRecurringActivityWithSpecificTimeAndSelectedRecurrencesPerDay =
    isRecurring &&
    selectedTimeCategory === timeOfDaySchema.enum.Specific &&
    selectedRecurrencesPerDay;
  const shouldShowTimeSlots =
    isOneTimeActivityWithSpecificTime ||
    isRecurringActivityWithSpecificTimeAndSelectedRecurrencesPerDay;

  const recurrencesPerDayAsNumber =
    isRecurringActivityWithSpecificTimeAndSelectedRecurrencesPerDay
      ? Number(selectedRecurrencesPerDay)
      : 1;

  useEffect(() => {
    if (isAnyTimeOfDay) {
      unregister(recurrencesPerDayFieldName);
      unregister(timeslotsFieldName);
      unregister(timeSensitivityFieldName);
    }
  }, [
    isAnyTimeOfDay,
    unregister,
    recurrencesPerDayFieldName,
    timeslotsFieldName,
    timeSensitivityFieldName,
  ]);

  const timeCategoryField = register(timeCategoryFieldName, {
    required: isInFinalValidationPhase
      ? {
          value: true,
          message: t`Välj tid på dagen`,
        }
      : undefined,
    onChange: () => {
      resetField(timeSensitivityFieldName);
      resetField(recurrencesPerDayFieldName);
      resetField(timeslotsFieldName);
    },
  });

  return (
    <>
      <RadioButtons
        legend={t`Tid på dagen`}
        errorMessage={timeCategoryError}
        orientation="horizontal"
      >
        <RadioButton
          label={{ text: t(timeOfDayDictionary.Specific.short) }}
          visualStyle="framed"
          value={timeOfDaySchema.enum.Specific}
          {...timeCategoryField}
        />
        <RadioButton
          label={{ text: t(timeOfDayDictionary.Any.short) }}
          visualStyle="framed"
          value={timeOfDaySchema.enum.Any}
          {...timeCategoryField}
        />
      </RadioButtons>
      {isRecurring && selectedTimeCategory === timeOfDaySchema.enum.Specific ? (
        <RadioButtons
          legend={t`Antal tillfällen per dygn`}
          orientation="horizontal"
          errorMessage={recurrencesPerDayError}
          size={size}
        >
          {recurrencesPerDayOptions.map((recurrencesPerDay) => (
            <RadioButton
              key={recurrencesPerDay}
              label={{ text: recurrencesPerDay }}
              {...register(recurrencesPerDayFieldName, {
                required: isInFinalValidationPhase
                  ? {
                      value: true,
                      message: t`Välj antal tillfällen per dygn`,
                    }
                  : undefined,
              })}
              value={recurrencesPerDay}
              visualStyle="framed"
            />
          ))}
        </RadioButtons>
      ) : null}
      {shouldShowTimeSlots ? (
        <>
          <TemplateTimeSlots
            numberOfTimeslots={recurrencesPerDayAsNumber}
            size={size}
          />
          <TemplateTimeSensitivityPicker size={size} />
        </>
      ) : null}
    </>
  );
};
